import { FC, useEffect } from 'react';
import { Box } from '@zoopla-eng/zds-components/box';
import { triggerTracking } from '@zoopla/tracking';
import { GoogleMedium, FacebookMedium } from '@zoopla-eng/zds-components/icons';
import { ActionAfterAuth } from '../../AuthModal.types';
import { useSocialSignIn } from './hooks/useSocialSignIn/useSocialSignIn';
import ErrorAlert from '../../ErrorAlert/ErrorAlert';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';
import {
    customGridStyle,
    signInErrorMessageStyle,
    socialSignInTextStyle,
    socialSignInTextWrapperStyle
} from '../../AuthModal.styles.css';
import { fontGroups } from '@zoopla-eng/zds-components/themes';

type SocialCopy = {
    cta?: string;
    facebook: string;
    google: string;
};

export type SocialAuthComponentProps = {
    actionAfterAuth: ActionAfterAuth;
    copy: SocialCopy;
    type: 'signin' | 'register';
    shouldReset?: boolean;
    resetErrors?: () => void;
    variant?: 'horizontal' | 'vertical';
    onClickTracking?: (clickType: 'facebook' | 'google') => void;
    isRememberMeChecked?: boolean;
};

const SocialAuth: FC<React.PropsWithChildren<SocialAuthComponentProps>> = ({
    actionAfterAuth,
    copy,
    type,
    shouldReset,
    resetErrors,
    onClickTracking,
    isRememberMeChecked,
    variant = 'horizontal'
}) => {
    const [
        facebookError,
        onFacebookRegister,
        isFacebookAuthenticating,
        resetFacebookError
    ] = useSocialSignIn({
        type: 'facebook',
        actionAfterAuth
    });

    const [
        googleError,
        onGoogleRegister,
        isGoogleAuthenticating,
        resetGoogleError
    ] = useSocialSignIn({
        type: 'google',
        actionAfterAuth
    });

    useEffect(() => {
        if (isFacebookAuthenticating || isGoogleAuthenticating) {
            resetErrors?.();
            return;
        }

        if (shouldReset) {
            resetGoogleError();
            resetFacebookError();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFacebookAuthenticating, isGoogleAuthenticating, shouldReset]);

    const socialError = googleError || facebookError;

    return (
        <>
            <ErrorAlert hasError={!!socialError}>
                {socialError && (
                    <Box pb="S400">
                        <p
                            className={`${signInErrorMessageStyle} ${fontGroups.spoon}`}
                        >
                            {socialError}
                        </p>
                    </Box>
                )}
            </ErrorAlert>
            <div className={customGridStyle({ direction: variant })}>
                <ButtonLeft
                    Icon={GoogleMedium}
                    category="secondary"
                    data-testid={`${type}-google`}
                    onClick={() => {
                        onGoogleRegister(isRememberMeChecked);

                        if (onClickTracking) {
                            onClickTracking('google');
                            return;
                        }

                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: type,
                            eventAction: 'click',
                            eventLabel: 'google'
                        });
                    }}
                    loading={isGoogleAuthenticating}
                    loadingText="Loading"
                >
                    {copy.google}
                </ButtonLeft>
                <ButtonLeft
                    Icon={FacebookMedium}
                    category="secondary"
                    data-testid={`${type}-facebook`}
                    onClick={() => {
                        onFacebookRegister(isRememberMeChecked);

                        if (onClickTracking) {
                            onClickTracking('facebook');
                            return;
                        }

                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: type,
                            eventAction: 'click',
                            eventLabel: 'facebook'
                        });
                    }}
                    loading={isFacebookAuthenticating}
                    loadingText="Loading"
                >
                    {copy.facebook}
                </ButtonLeft>
            </div>
            {copy.cta && (
                <Box pb="M300" pt="M200">
                    <div className={socialSignInTextWrapperStyle}>
                        <Box px="S200">
                            <p
                                className={`${socialSignInTextStyle} ${fontGroups.fork}`}
                            >
                                {copy.cta}
                            </p>
                        </Box>
                    </div>
                </Box>
            )}
        </>
    );
};

export default SocialAuth;
